import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import { Send as SendIcon, Refresh as RefreshIcon, Payments as PaymentsIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import { defaultConfig } from '../config/bundlerConfig';
import { useAuth } from '@clerk/clerk-react';
import { useProject } from '../projects/ProjectContext';
import { ethereumService } from '../utils/ethereum';

const MotionPaper = motion(Paper);

/** Only sweep wallets with native ETH balance strictly greater than this (gas + cushion still deducted afterward). */
const REFUND_ELIGIBLE_MIN_ETH = '0.0002';

const pillSx = {
  borderRadius: 999,
  px: 2,
  fontWeight: 750,
  textTransform: 'none',
};

const gradientPrimarySx = {
  ...pillSx,
  backgroundImage:
    'linear-gradient(90deg, rgba(168, 85, 247, 1) 0%, rgba(99, 102, 241, 1) 45%, rgba(34, 211, 238, 1) 100%)',
  color: '#08090e',
  boxShadow: '0 8px 28px rgba(168, 85, 247, 0.26)',
  '&:hover': {
    backgroundImage:
      'linear-gradient(90deg, rgba(187, 110, 255, 1) 0%, rgba(115, 122, 255, 1) 45%, rgba(56, 225, 255, 1) 100%)',
    boxShadow: '0 10px 32px rgba(168, 85, 247, 0.32)',
  },
  '&.Mui-disabled': {
    backgroundImage: 'none',
    color: alpha('#e9e7ff', 0.42),
    boxShadow: 'none',
  },
};

const tableContainerSx = {
  borderRadius: 2.5,
  border: `1px solid ${alpha('#fff', 0.1)}`,
  backgroundColor: alpha('#000', 0.25),
};

const headCellSx = {
  fontWeight: 800,
  bgcolor: alpha('#0a0c12', 0.95),
  borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
};

function uniqByAddress(list) {
  const seen = new Set();
  const out = [];
  for (const w of list) {
    const a = (w?.address || '').toLowerCase();
    if (!a || seen.has(a)) continue;
    seen.add(a);
    out.push(w);
  }
  return out;
}

const Refund = () => {
  const theme = useTheme();
  const accentPurple = theme.palette.primary.main;
  const accentCyan = theme.palette.secondary.main;

  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);
  const { currentProjectId } = useProject();

  const [destination, setDestination] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const apiFetch = useCallback(
    async (path, init = {}) => {
      const token = await getTokenRef.current();
      if (!token) throw new Error('Not signed in');
      if (!currentProjectId) throw new Error('Select a project first');

      const res = await fetch(`${defaultConfig.SERVER_URL.url}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
          'X-Project-Id': currentProjectId,
        },
      });
      const text = await res.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
      return data;
    },
    [currentProjectId],
  );

  const loadWalletSources = useCallback(async () => {
    const list = [];

    const wallets = await ethereumService.loadWallets();
    for (const w of wallets || []) {
      if (w?.address && w?.privateKey) list.push({ address: w.address, privateKey: w.privateKey, label: 'wallet' });
    }

    try {
      const master = await apiFetch('/api/me/master-wallet/export');
      if (master?.address && master?.privateKey) list.push({ address: master.address, privateKey: master.privateKey, label: 'master' });
    } catch {
      // ignore
    }

    try {
      const creation = await apiFetch('/api/me/creation-wallet/export');
      if (creation?.address && creation?.privateKey) list.push({ address: creation.address, privateKey: creation.privateKey, label: 'creation' });
    } catch {
      // ignore
    }

    return uniqByAddress(list);
  }, [apiFetch]);

  const handlePreview = useCallback(async () => {
    const to = destination.trim();
    if (!ethers.utils.isAddress(to)) return toast.error('Enter a valid destination address');

    setLoading(true);
    try {
      await ethereumService.ensureInitialized();
      const provider = ethereumService.provider;
      const sources = await loadWalletSources();
      const balances = await Promise.all(
        sources.map(async (s) => {
          const bal = await provider.getBalance(s.address);
          return { ...s, balanceWei: bal, balanceEth: ethers.utils.formatEther(bal) };
        }),
      );

      const totalWei = balances.reduce((sum, b) => sum.add(b.balanceWei), ethers.BigNumber.from(0));
      const nonZero = balances.filter((b) => b.balanceWei.gt(0));
      const thresholdWei = ethers.utils.parseEther(REFUND_ELIGIBLE_MIN_ETH);
      const eligibleRefund = balances.filter((b) => b.balanceWei.gt(thresholdWei));
      setPreview({
        destination: to,
        sources: balances,
        totalEth: ethers.utils.formatEther(totalWei),
        nonZeroCount: nonZero.length,
        eligibleRefundCount: eligibleRefund.length,
        refundMinEth: REFUND_ELIGIBLE_MIN_ETH,
      });
      setLastResult(null);
    } catch (e) {
      toast.error(e?.message || 'Failed to load balances');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [destination, loadWalletSources]);

  const handleRefundAll = useCallback(async () => {
    const to = destination.trim();
    if (!ethers.utils.isAddress(to)) return toast.error('Enter a valid destination address');

    setLoading(true);
    try {
      await ethereumService.ensureInitialized();
      const provider = ethereumService.provider;
      const feeData = await provider.getFeeData().catch(() => ({}));
      const gasLimit = ethers.BigNumber.from(21000);
      const use1559 = !!feeData?.maxFeePerGas;
      const minBalanceWei = ethers.utils.parseEther(REFUND_ELIGIBLE_MIN_ETH);

      const sources = await loadWalletSources();
      if (sources.length === 0) throw new Error('No wallets found to refund');

      const signedTxs = [];
      const results = [];
      const network = await provider.getNetwork();

      for (const s of sources) {
        const pk = s.privateKey.startsWith('0x') ? s.privateKey : `0x${s.privateKey}`;
        const signer = new ethers.Wallet(pk, provider);
        const balance = await provider.getBalance(signer.address);

        if (!balance.gt(minBalanceWei)) {
          results.push({
            ...s,
            from: signer.address,
            success: false,
            reason: `Balance must exceed ${REFUND_ELIGIBLE_MIN_ETH} ETH`,
            amountEth: '0',
            txHash: null,
          });
          continue;
        }

        const gasPrice = use1559 ? feeData.maxFeePerGas : feeData.gasPrice || (await provider.getGasPrice());
        const gasCost = gasLimit.mul(gasPrice);
        const cushion = ethers.utils.parseEther('0.00005');
        const refundable = balance.sub(gasCost).sub(cushion);
        if (refundable.lte(0)) {
          results.push({ ...s, from: signer.address, success: false, reason: 'Balance too low after gas', amountEth: '0', txHash: null });
          continue;
        }

        const nonce = await signer.getTransactionCount('pending');
        const txReq = {
          to,
          value: refundable,
          gasLimit,
          chainId: network.chainId,
          nonce,
          ...(use1559 ? { type: 2 } : { type: 0 }),
          ...(use1559 ? { maxFeePerGas: feeData.maxFeePerGas } : {}),
          ...(use1559 && feeData?.maxPriorityFeePerGas ? { maxPriorityFeePerGas: feeData.maxPriorityFeePerGas } : {}),
          ...(!use1559 ? { gasPrice } : {}),
        };
        const signed = await signer.signTransaction(txReq);
        const parsed = ethers.utils.parseTransaction(signed);
        const txHash = parsed?.hash || null;
        signedTxs.push(signed);
        results.push({ ...s, from: signer.address, success: true, reason: null, amountEth: ethers.utils.formatEther(refundable), txHash });
      }

      if (signedTxs.length === 0) {
        toast.info(
          `Nothing to sweep: no wallet had balance above ${REFUND_ELIGIBLE_MIN_ETH} ETH, or amounts were too low after gas + cushion.`,
        );
        setLastResult({ destination: to, submitted: 0, bundleHash: null, results });
        await handlePreview();
        return;
      }

      const relayRes = await ethereumService.sendSignedTransactionsViaFlashbots(signedTxs, {
        revertingTxHashes: [],
      });
      const bundleHash = relayRes?.bundleHash || relayRes?.result?.bundleHash || relayRes?.result?.bundle_hash || null;

      toast.success(
        `BlockRazor refund bundle submitted. Transfers: ${signedTxs.length}${bundleHash ? ` • Bundle: ${String(bundleHash).slice(0, 10)}…` : ''}`,
      );
      setLastResult({ destination: to, submitted: signedTxs.length, bundleHash, results });
      await handlePreview();
    } catch (e) {
      toast.error(e?.message || 'Refund failed');
    } finally {
      setLoading(false);
    }
  }, [destination, handlePreview, loadWalletSources]);

  const labelChip = (label) => {
    const map = {
      wallet: { color: alpha(accentCyan, 0.85), bg: alpha(accentCyan, 0.12), border: alpha(accentCyan, 0.35) },
      master: { color: alpha(accentPurple, 0.95), bg: alpha(accentPurple, 0.12), border: alpha(accentPurple, 0.35) },
      creation: { color: '#e0e7ff', bg: alpha('#818cf8', 0.14), border: alpha('#818cf8', 0.32) },
    };
    const s = map[label] || map.wallet;
    return (
      <Chip
        size="small"
        label={label}
        sx={{
          fontWeight: 780,
          textTransform: 'capitalize',
          height: 24,
          color: s.color,
          bgcolor: s.bg,
          border: `1px solid ${s.border}`,
        }}
      />
    );
  };

  return (
    <Box sx={{ maxWidth: 920, mx: 'auto' }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 900,
          letterSpacing: 0.6,
          backgroundImage: `linear-gradient(90deg, ${accentPurple} 0%, #6366f1 45%, ${accentCyan} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        Refund
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2.5, maxWidth: 640 }}>
        Sweep ETH from project wallets over {REFUND_ELIGIBLE_MIN_ETH} ETH (wallet list + master + creation wallet) via a BlockRazor bundle into one destination address.
      </Typography>

      <Alert
        severity="warning"
        sx={{
          mb: 2.5,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.warning.main, 0.1),
          border: `1px solid ${alpha(theme.palette.warning.main, 0.32)}`,
          color: alpha(theme.palette.warning.light, 0.98),
          '& .MuiAlert-icon': { color: theme.palette.warning.light },
        }}
      >
        Double-check the destination address. Refunds are on-chain transfers and <strong>cannot be reversed</strong>.
      </Alert>

      <MotionPaper
        elevation={0}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${alpha(accentPurple, 0.22)}`,
          background: `linear-gradient(165deg, ${alpha(accentPurple, 0.06)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 46%, ${alpha(accentCyan, 0.04)} 100%)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Stack divider={preview ? <Divider flexItem sx={{ borderColor: alpha('#fff', 0.06) }} /> : null}>
          <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  bgcolor: alpha(accentPurple, 0.14),
                  border: `1px solid ${alpha(accentPurple, 0.3)}`,
                  color: '#ede9fe',
                }}
              >
                <PaymentsIcon sx={{ opacity: 0.95 }} fontSize="small" />
              </Box>
              <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 880 }}>
                    Sweep to address
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Preview loads live balances; Refund all signs sweeps only for wallets with balance &gt; {REFUND_ELIGIBLE_MIN_ETH} ETH and submits a strict BlockRazor bundle.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                  <TextField
                    label="Destination address"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="0x..."
                    fullWidth
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': { borderRadius: 2 },
                    }}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                    <Button
                      variant="outlined"
                      onClick={() => void handlePreview()}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      sx={{ ...pillSx, borderColor: alpha(accentPurple, 0.45), py: 0.95 }}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() => void handleRefundAll()}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={18} sx={{ color: '#08090e' }} /> : <SendIcon sx={{ color: '#08090e' }} />}
                      sx={{ ...gradientPrimarySx, py: 0.95 }}
                    >
                      Refund all
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {preview ? (
            <Box sx={{ p: { xs: 2, sm: 2.5 }, pt: { xs: 0, sm: 0 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 880, mb: 1.25 }}>
                Balance preview
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ mb: 2 }}>
                <Chip
                  size="small"
                  label={`${preview.sources.length} source${preview.sources.length === 1 ? '' : 's'}`}
                  sx={{ fontWeight: 760, borderRadius: 2, border: `1px solid ${alpha(accentPurple, 0.35)}`, bgcolor: alpha(accentPurple, 0.08) }}
                />
                <Chip
                  size="small"
                  label={`${preview.nonZeroCount} non‑zero`}
                  sx={{ fontWeight: 760, borderRadius: 2, border: `1px solid ${alpha(accentCyan, 0.35)}`, bgcolor: alpha(accentCyan, 0.1) }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${preview.eligibleRefundCount ?? 0} sweep‑eligible`}
                  sx={{ fontWeight: 760, borderRadius: 2, borderColor: alpha(accentPurple, 0.4) }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Total ${Number(preview.totalEth).toFixed(6)} ETH`}
                  sx={{ fontWeight: 700, borderRadius: 2, borderColor: alpha('#fff', 0.14) }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                → {preview.destination}
              </Typography>

              <TableContainer sx={tableContainerSx}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Type</TableCell>
                      <TableCell sx={headCellSx}>Address</TableCell>
                      <TableCell align="right" sx={headCellSx}>
                        ETH
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.sources.map((s) => (
                      <TableRow
                        key={s.address}
                        hover
                        sx={{
                          '&:nth-of-type(even) td': { bgcolor: alpha('#fff', 0.02) },
                          '& td': { py: 1.15, borderColor: alpha('#fff', 0.06) },
                        }}
                      >
                        <TableCell>{labelChip(s.label)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 720 }}>
                            {s.address.slice(0, 6)}…{s.address.slice(-4)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 760, color: alpha(accentCyan, 0.95) }}>
                            {Number(s.balanceEth).toFixed(6)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {lastResult ? (
                <>
                  <Divider sx={{ my: 2.5, borderColor: alpha('#fff', 0.08) }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 880, mb: 1 }}>
                    Last refund run
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    Destination: {lastResult.destination}
                    {lastResult.bundleHash ? ` • Bundle: ${String(lastResult.bundleHash)}` : ''}
                  </Typography>

                  <TableContainer sx={{ ...tableContainerSx, mt: 1.5 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headCellSx}>Type</TableCell>
                          <TableCell sx={headCellSx}>From</TableCell>
                          <TableCell align="right" sx={headCellSx}>
                            Amount (ETH)
                          </TableCell>
                          <TableCell sx={headCellSx}>Tx hash</TableCell>
                          <TableCell sx={headCellSx}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lastResult.results.map((r) => (
                          <TableRow
                            key={`${r.label}.${r.address}`}
                            hover
                            sx={{
                              '&:nth-of-type(even) td': { bgcolor: alpha('#fff', 0.02) },
                              '& td': { py: 1.1, borderColor: alpha('#fff', 0.06) },
                            }}
                          >
                            <TableCell>{labelChip(r.label)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 720 }}>
                                {String(r.from || r.address).slice(0, 6)}…{String(r.from || r.address).slice(-4)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{Number(r.amountEth || 0).toFixed(6)}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.txHash ? `${String(r.txHash).slice(0, 10)}…` : '—'}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 760,
                                  color: r.success ? alpha(accentCyan, 0.95) : alpha(theme.palette.warning.light, 0.9),
                                }}
                              >
                                {r.success ? 'Signed' : r.reason || 'Skipped'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : null}
            </Box>
          ) : null}
        </Stack>
      </MotionPaper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: { xs: 'left', sm: 'center' } }}>
        Wallets need more than {REFUND_ELIGIBLE_MIN_ETH} ETH native balance to be swept; estimated gas (~21k transfer) and a small cushion are deducted from each before sending the bundle to BlockRazor.
      </Typography>
    </Box>
  );
};

export default Refund;
